import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const { user } = useAuth();
    const debugInfo = { steps: [] };

    // Debug logging function
    const log = (message, data = null) => {
        console.log(`[ProtectedRoute] ${message}`, data || '');
        debugInfo.steps.push(message);
        if (data) debugInfo.steps.push(JSON.stringify(data));
    };

    useEffect(() => {
        const loginDebugInfo = sessionStorage.getItem('debugInfo');
        if (loginDebugInfo) {
            log('Debug info from login:', JSON.parse(loginDebugInfo));
            sessionStorage.removeItem('debugInfo');
        }
    }, []);

    // Check token
    const token = localStorage.getItem('token');
    log('Token check:', token ? 'Token exists' : 'No token');

    if (!token) {
        log('No token found - redirecting to login');
        sessionStorage.setItem('protectedRouteDebug', JSON.stringify(debugInfo));
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Verify user data and token validity
    if (!user) {
        log('No user data in context - redirecting to login');
        sessionStorage.setItem('protectedRouteDebug', JSON.stringify(debugInfo));
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Handle admin routes
    if (location.pathname.startsWith('/admin')) {
        log('Checking admin access:', { isAdmin: user.isAdmin });
        if (!user.isAdmin) {
            log('User is not an admin - redirecting to shop');
            sessionStorage.setItem('protectedRouteDebug', JSON.stringify(debugInfo));
            return <Navigate to="/shop" replace />;
        }
    }

    // Handle subscription-based routes
    if (location.pathname.startsWith('/viewTests') || location.pathname.startsWith('/noteCards')) {
        const subscriptionId = location.pathname.split('/')[2];
        log('Checking subscription access for path:', location.pathname);
        log('Extracted subscriptionId:', subscriptionId);

        // Get user's subscriptions from API response if available
        const userSubscriptions = user.subscriptions || [];
        log('Available subscriptions:', userSubscriptions);

        let hasValidSubscription = userSubscriptions.some((subscription) => {
            const isMatchingId = subscription._id === subscriptionId;
            const isActive = new Date(subscription.endDate) > new Date();

            log('Checking subscription:', {
                subscriptionId: subscription._id,
                endDate: subscription.endDate,
                isMatchingId,
                isActive,
            });

            return isMatchingId && isActive;
        });

        log('Subscription validation result:', { hasValidSubscription });

        if (!hasValidSubscription) {
            // Check URL parameters for test ID
            const urlParams = new URLSearchParams(location.search);
            const testId = urlParams.get('test');
            log('Test ID from URL:', testId);

            // Double-check if there's a valid subscription for this test
            hasValidSubscription = userSubscriptions.some(
                (subscription) => subscription.product?.[0]?.allowedTest === testId && new Date(subscription.endDate) > new Date(),
            );

            if (!hasValidSubscription) {
                log('No valid subscription found - redirecting to subscriptions');
                sessionStorage.setItem('protectedRouteDebug', JSON.stringify(debugInfo));
                return <Navigate to="/subscriptions" replace />;
            }
        }
    }

    log('Access granted - rendering protected route');
    sessionStorage.setItem('protectedRouteDebug', JSON.stringify(debugInfo));
    return children;
};

export default ProtectedRoute;
